// Company name patterns that indicate a corporate entity
const COMPANY_SUFFIXES = [
  // UK
  'ltd', 'limited', 'plc', 'llp', 'lp', 'cic',
  // US
  'inc', 'incorporated', 'corp', 'corporation', 'llc', 'l.l.c.',
  // Europe
  'gmbh', 'ag', 'sa', 's.a.', 'bv', 'b.v.', 'nv', 'n.v.',
  'sarl', 's.a.r.l.', 'sas', 's.a.s.', 'se', 'sprl', 'bvba',
  'srl', 's.r.l.', 'spa', 's.p.a.', 'snc', 's.n.c.',
  'ab', 'oy', 'oyj', 'as', 'asa', 'aps', 'a/s',
  // Other
  'pty', 'pte', 'bhd', 'sdn bhd', 'co.', 'company',
  'holdings', 'group', 'partners', 'capital', 'ventures', 'investments',
  'fund', 'trust', 'foundation'
];

// Words that commonly appear in company names
const COMPANY_KEYWORDS = [
  'holdings', 'group', 'partners', 'capital', 'ventures', 
  'investments', 'fund', 'trust', 'foundation', 'associates',
  'international', 'global', 'enterprises', 'industries',
  'solutions', 'services', 'consultants', 'management'
];

/**
 * Detects if a name belongs to a company rather than an individual
 */
export function isCompanyName(name: string): boolean {
  if (!name || name.trim().length === 0) return false;
  
  const normalizedName = name.toLowerCase().trim();
  const words = normalizedName.split(/\s+/);
  
  // Check if any word matches a company suffix
  for (const word of words) {
    const cleanWord = word.replace(/[.,]/g, '');
    if (COMPANY_SUFFIXES.includes(cleanWord)) {
      return true;
    }
  }
  
  // Check for common company name patterns
  for (const keyword of COMPANY_KEYWORDS) {
    if (normalizedName.includes(keyword)) {
      return true;
    }
  }
  
  // Check for patterns like "& Co" or "& Company"
  if (/&\s*(co\.?|company|partners|sons|associates)/i.test(normalizedName)) {
    return true;
  }
  
  return false;
}

/**
 * Extract country code from address
 */
export function getCountryCodeFromAddress(country: string): string {
  const countryMap: Record<string, string> = {
    'united kingdom': 'GB',
    'uk': 'GB',
    'england': 'GB',
    'scotland': 'GB',
    'wales': 'GB',
    'northern ireland': 'GB',
    'united states': 'US',
    'usa': 'US',
    'united states of america': 'US',
    'germany': 'DE',
    'deutschland': 'DE',
    'france': 'FR',
    'spain': 'ES',
    'italy': 'IT',
    'netherlands': 'NL',
    'belgium': 'BE',
    'switzerland': 'CH',
    'ireland': 'IE',
    'australia': 'AU',
    'canada': 'CA',
    'singapore': 'SG',
    'hong kong': 'HK',
    'japan': 'JP',
    'china': 'CN',
  };
  
  const normalized = country.toLowerCase().trim();
  return countryMap[normalized] || 'GB';
}

interface MockCompanyData {
  directors: Array<{
    name: string;
    dateOfBirth: string;
    nationality: string;
    address: {
      street: string;
      city: string;
      postalCode: string;
      country: string;
    };
    appointedDate: string;
    occupation: string;
  }>;
  shareholders: Array<{
    name: string;
    shareholding: number;
    shareClass: string;
    address: {
      street: string;
      city: string;
      postalCode: string;
      country: string;
    };
    type: 'individual' | 'company';
  }>;
  registrationNumber: string;
}

/**
 * Mock function to look up company details from registry
 * In production, this would call a real API
 */
export async function lookupCompanyDetails(
  companyName: string, 
  countryCode: string,
  registrationNumber?: string
): Promise<MockCompanyData | null> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  // Generate deterministic mock data based on company name
  const nameHash = companyName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  const mockDirectorNames = [
    'James Wilson', 'Emma Thompson', 'David Brown', 'Sophie Taylor',
    'Michael Davies', 'Charlotte Anderson', 'Robert White', 'Olivia Martin'
  ];
  
  const mockShareholderData = [
    { name: 'Alpha Holdings Ltd', shareholding: 40, type: 'company' as const },
    { name: 'Richard Sterling', shareholding: 35, type: 'individual' as const },
    { name: 'Margaret Chen', shareholding: 25, type: 'individual' as const },
  ];
  
  const directors = [
    {
      name: mockDirectorNames[nameHash % mockDirectorNames.length],
      dateOfBirth: '1970-05-20',
      nationality: countryCode === 'GB' ? 'British' : 'American',
      address: {
        street: '78 Corporate Way',
        city: countryCode === 'GB' ? 'London' : 'New York',
        postalCode: countryCode === 'GB' ? 'SW1A 2AA' : '10001',
        country: countryCode === 'GB' ? 'United Kingdom' : 'United States',
      },
      appointedDate: '2015-03-15',
      occupation: 'Director',
    },
    {
      name: mockDirectorNames[(nameHash + 3) % mockDirectorNames.length],
      dateOfBirth: '1978-11-08',
      nationality: countryCode === 'GB' ? 'British' : 'American',
      address: {
        street: '22 Business Square',
        city: countryCode === 'GB' ? 'Manchester' : 'Chicago',
        postalCode: countryCode === 'GB' ? 'M1 2AB' : '60601',
        country: countryCode === 'GB' ? 'United Kingdom' : 'United States',
      },
      appointedDate: '2018-07-01',
      occupation: 'Finance Director',
    },
  ];
  
  const shareholders = mockShareholderData.map((s, i) => ({
    ...s,
    shareClass: 'Ordinary',
    address: {
      street: `${(i + 1) * 50} Shareholder Street`,
      city: countryCode === 'GB' ? 'London' : 'New York',
      postalCode: countryCode === 'GB' ? `EC${i + 1}A ${i + 1}BB` : `1000${i + 1}`,
      country: countryCode === 'GB' ? 'United Kingdom' : 'United States',
    },
  }));
  
  // Generate a mock registration number if not provided
  const regNum = registrationNumber || `${(nameHash % 9000000) + 1000000}`;
  
  return {
    directors,
    shareholders,
    registrationNumber: regNum,
  };
}
